import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Plus, Users, Send, Edit, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { GroupEditorDialog, GROUP_COLORS } from './GroupEditorDialog';

const GroupCard = ({ g, users, onWrite, onEdit, onDelete, focused }) => {
  const names = g.member_ids.map((id) => users.find((u) => u.id === id)).filter(Boolean);
  return (
    <div id={`group-card-${g.id}`} className={`bg-slate-800 rounded-xl p-5 border transition-colors flex flex-col ${focused ? 'border-amber-400 ring-2 ring-amber-400/50' : 'border-slate-700 hover:border-amber-500/40'}`} data-testid={`group-card-${g.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <span className={`w-3 h-3 rounded-full mt-1.5 ${GROUP_COLORS[g.color] || GROUP_COLORS.amber}`} />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-white truncate">{g.name}</h3>
          {g.description && <p className="text-sm text-slate-400 line-clamp-2">{g.description}</p>}
        </div>
        <span className="text-xs px-2 py-1 rounded bg-slate-700 text-slate-200" data-testid={`group-count-${g.id}`}>{g.member_count} membri</span>
      </div>
      <p className="text-xs text-slate-500 mb-4 line-clamp-2 flex-1">
        {names.length ? names.slice(0, 6).map((u) => u.full_name || u.username).join(', ') + (names.length > 6 ? ` e altri ${names.length - 6}` : '') : 'Nessun membro'}
      </p>
      <div className="flex gap-2">
        <Button onClick={onWrite} disabled={!g.member_count} size="sm" className="bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold flex-1" data-testid={`group-write-${g.id}`}>
          <Send className="w-4 h-4 mr-1.5" /> Scrivi al gruppo
        </Button>
        <Button onClick={onEdit} variant="ghost" size="sm" className="text-blue-400 hover:bg-blue-500/20" data-testid={`group-edit-${g.id}`}><Edit className="w-4 h-4" /></Button>
        <Button onClick={onDelete} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/20" data-testid={`group-delete-${g.id}`}><Trash2 className="w-4 h-4" /></Button>
      </div>
    </div>
  );
};

export const AdminGroupsTab = ({ groups, users, backendUrl, token, refreshGroups, showToast, openBroadcast, focusGroupId }) => {
  const [editor, setEditor] = useState({ open: false, group: null });
  useEffect(() => {
    if (focusGroupId) document.getElementById(`group-card-${focusGroupId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [focusGroupId, groups.length]);
  const headers = { Authorization: `Bearer ${token}` };

  const save = async (payload) => {
    try {
      if (editor.group) await axios.put(`${backendUrl}/api/admin/groups/${editor.group.id}`, payload, { headers });
      else await axios.post(`${backendUrl}/api/admin/groups`, payload, { headers });
      showToast('success', 'Gruppo salvato');
      setEditor({ open: false, group: null });
      refreshGroups();
    } catch (e) {
      showToast('error', e.response?.data?.detail || 'Errore salvataggio gruppo');
    }
  };

  const remove = async (g) => {
    if (!window.confirm(`Eliminare il gruppo "${g.name}"? Gli utenti non verranno cancellati.`)) return;
    await axios.delete(`${backendUrl}/api/admin/groups/${g.id}`, { headers }).catch(() => {});
    refreshGroups();
  };

  return (
    <div data-testid="groups-tab">
      <div className="flex flex-wrap justify-between items-center gap-3 mb-6">
        <div>
          <h2 className="text-lg font-semibold text-white">Gruppi di utenti</h2>
          <p className="text-sm text-slate-400">Raggruppa clienti e lead (es. per azienda) e scrivi a tutti con un solo invio.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => openBroadcast({})} className="bg-slate-700 hover:bg-slate-600" data-testid="groups-broadcast-btn">
            <Send className="w-4 h-4 mr-2" /> Invio multiplo
          </Button>
          <Button onClick={() => setEditor({ open: true, group: null })} className="bg-green-600 hover:bg-green-700" data-testid="add-group-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuovo gruppo
          </Button>
        </div>
      </div>
      {groups.length === 0 ? (
        <div className="text-center py-14 bg-slate-800/50 rounded-xl" data-testid="groups-empty">
          <Users className="w-12 h-12 text-slate-600 mx-auto mb-4" />
          <p className="text-slate-400">Nessun gruppo ancora. Crea il primo, ad esempio "Microsoft".</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {groups.map((g) => (
            <GroupCard
              key={g.id} g={g} users={users}
              onWrite={() => openBroadcast({ groupIds: [g.id] })}
              onEdit={() => setEditor({ open: true, group: g })}
              onDelete={() => remove(g)}
              focused={g.id === focusGroupId}
            />
          ))}
        </div>
      )}
      <GroupEditorDialog open={editor.open} group={editor.group} users={users} onClose={() => setEditor({ open: false, group: null })} onSave={save} />
    </div>
  );
};
